<i18n src="./translations"></i18n>

<template>
  <div :class="classes" :data-preload="[$options.name]">
    <div v-if="productsCount > 0">
      <div v-if="isSearchPage" class="title">
        <PageTitle is-search-page :modifiers="['embedded', 'wrapping']" />
      </div>

      <GenArtFilterContainer
        v-if="genartAvailable"
        :options="allFilters['genart_name'].options"
        :value="storedActiveFilters['genart_name'] || []"
        :is-vehicle-selected="isVehicleSelected"
        :resolve-target-url="genartPillTargetUrl"
        @input="handleGenartFilter"
      />

      <div class="filters">
        <CatalogCategoryFilters
          v-if="shouldShowFilters"
          :filter-sections="sections"
          :all-filters="allFilters"
          :active-filters="storedActiveFilters"
          :active-sorting="storedActiveSorting"
          @applyFilters="handleApplyFilters"
        />
      </div>
    </div>

    <CatalogOtcFilters
      :active="storedActiveFilters.whs && storedActiveFilters.whs[0]"
      @onSelect="onSelectWhs"
    />

    <div v-if="productsCount === 0">
      {{
        !showOtcNoResultsMessage
          ? $t('catalog-category-filters-wrapper.no-results-found')
          : $t('catalog-category-filters-wrapper.no-results-found-otc')
      }}
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import CatalogCategoryFilters from 'Components/03-organisms/catalog-category-filters/CatalogCategoryFilters';
import CatalogOtcFilters from 'Components/02-molecules/catalog-otc-filters/CatalogOtcFilters.vue';
import GenArtFilterContainer from 'Components/03-organisms/gen-art-filter-container/GenArtFilterContainer.vue';
import globalMixin from 'Libs/mixins/globalMixin';
import UrlHelper from 'Libs/helpers/url';
import get from 'lodash/get';
import size from 'lodash/size';
import PageTitle from 'Components/03-organisms/page-title/PageTitle';

export default {
  name: 'CatalogCategoryFiltersWrapper',
  components: {
    CatalogCategoryFilters,
    PageTitle,
    CatalogOtcFilters,
    GenArtFilterContainer,
  },
  mixins: [globalMixin],
  computed: {
    ...mapState('core', {
      isSearchPage: (state) => {
        const type = get(state, 'placeholders.content.data.plp.pageType');
        return type === 'search';
      },
    }),
    ...mapState('plpFilters', {
      productsCount: (data) => data.count,
      open: (data) => data.openMobileFilter,
      sections: (data) => data.filterSections || {},
      allFilters(data) {
        if (!data.filters) {
          return {};
        }

        data.filters.sorting.title = this.$t(
          'catalog-category-filters-wrapper.sort'
        );
        Object.values(data.filters.sorting.options).forEach((option) => {
          if (option.dir) {
            const directionLabel = this.$t(
              'catalog-category-filters-wrapper.' + option.dir
            );
            option.title = option.title.includes(directionLabel)
              ? option.title
              : `${directionLabel} ${option.title}`;
          } else {
            option.title = `${this.$t(
              'catalog-category-filters-wrapper.relevance'
            )}`;
          }
        });

        return data.filters;
      },
      storedActiveFilters: (data) => data.activeFilters || {},
      storedActiveSorting: (data) => data.activeSorting,
    }),
    ...mapState('carSelection', {
      isVehicleSelected: ({ data }) => !!data.carDisplayName,
    }),
    hasFilters() {
      return size(this.allFilters) > 0;
    },
    hasActiveFilters() {
      return size(this.storedActiveFilters) !== 0;
    },
    shouldShowFilters() {
      if (this.hasFilters) {
        if (this.productsCount < 2 && !this.hasActiveFilters) {
          return false;
        }
        return true;
      }
      return false;
    },
    genartAvailable() {
      return this.allFilters && this.allFilters.genart_name;
    },
    ...mapGetters('core', ['isOTCSalesChannel']),
    showOtcNoResultsMessage() {
      const whs = this.storedActiveFilters?.whs;
      return this.isOTCSalesChannel && Array.isArray(whs) && whs.length > 0;
    },
  },
  methods: {
    ...mapActions('plpFilters', [
      'openMobileFilter',
      'closeMobileFilter',
      'submitFilters',
    ]),
    handleClose() {
      this.$emit('close');
      this.closeMobileFilter();
    },
    handleApplyFilters(dirtyActiveFilters, dirtySorting) {
      this.submitFilters({
        newFilters: dirtyActiveFilters,
        newSorting: dirtySorting,
      });
    },
    genartPillTargetUrl(alias) {
      if (typeof window === 'undefined') return undefined;

      const filters = { ...this.storedActiveFilters, genart_name: [alias] };
      const sorting = this.storedActiveSorting || {};
      const { search } = UrlHelper.getUrlParameters();

      const parameters = {};
      Object.entries(filters).forEach(([key, values]) => {
        if (!Array.isArray(values)) return;
        values.forEach((value, index) => {
          parameters[
            encodeURIComponent(`${key}[${index}]`)
          ] = encodeURIComponent(value);
        });
      });

      parameters.page = '1';
      if (sorting.dir) parameters.sort_order = sorting.dir;
      parameters.sort = sorting.identifier;
      if (search) parameters.search = search;
      if (Array.isArray(filters.whs) && filters.whs.length > 0) {
        parameters.whs = encodeURIComponent(String(filters.whs[0]));
      }

      return UrlHelper.assembleUrl({ parameters, shouldMerge: false });
    },
    handleGenartFilter(value) {
      const newFilters = { ...this.storedActiveFilters };
      if (value && value.length > 0) {
        newFilters.genart_name = value;
      } else {
        delete newFilters.genart_name;
      }
      this.submitFilters({
        newFilters,
        newSorting: this.storedActiveSorting,
      });
    },
    onSelectWhs(value) {
      const newFilters = { ...this.storedActiveFilters };
      if (value === null || value === undefined) {
        delete newFilters.whs;
      } else {
        newFilters.whs = [value];
      }
      this.submitFilters({
        newFilters,
        newSorting: this.storedActiveSorting,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'mixins';

.catalogCategoryFiltersWrapper {
  display: block;

  .no-items {
    padding-top: 15px;
  }

  .title {
    padding-bottom: 12px;

    @media #{$_mediaMUp} {
      padding-top: 6px;
    }

    @media #{$_mediaMDown} {
      padding: var(--space-2) 0;
    }

    @media #{$_mediaLUp} {
      padding-top: 10px;
      padding-bottom: 26px;
      flex-wrap: nowrap;
    }
  }

  @media #{$_mediaMUp} {
    margin-bottom: var(--space-1);
  }
}
</style>
